import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

import CasinoCRMApi from '../../../../casino/Api/CRM/CasinoCRMApi';
import CasinoAffiliateResponse, {
    CasinoAffiliateSummaryResponse,
} from '../../../../casino/Api/CRM/Responses/CasinoAffiliateResponse';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { formatDate } from '../../../../utils/formatters';
import DateSelectRange from '../../../elements/DateSelectRange';
import Loader from '../../../elements/Loader';
import Table from '../../../elements/Table';
import Card from '../../../elements/common/Card';
import TableLoader from '../../../elements/common/TableLoader';
import Paginator from '../../../elements/paginator';
import { TimeRangeName } from '../../../helpers/ShowMetrics';
import useAutoPaginator from '../../../hooks/useAutoPaginator';
import useNumberParams from '../../../hooks/useNumberParams';

import QuestionMark from 'crm/elements/common/QuestionMark';

function CasinoAffiliateCommissions() {
    const { id } = useNumberParams();
    const [range, setRange] = useState<TimeRangeName>('this-month');
    const [startDate, setStartDate] = useState<Moment>(moment().startOf('month'));
    const [endDate, setEndDate] = useState<Moment>(moment().endOf('month'));
    const [summary, setSummary] = useState<CasinoAffiliateSummaryResponse>({
        sum_daily_commission_manager: 0,
        sum_ggr_manager: 0,
    });

    const { items, updateItems, isLoading, auto } = useAutoPaginator<CasinoAffiliateResponse>((page, params) => {
        return CasinoCRMApi.affiliates.getPaginatedByAffiliateId(page, id, {
            params: {
                ...params,
                start: startDate?.isValid() ? startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss') : null,
                end: endDate?.isValid() ? endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss') : null,
            },
        });
    });

    useEffect((): void => {
        if (!endDate) {
            return;
        }

        updateItems();

        CasinoCRMApi.affiliates
            .getSummaryByAffiliateId(id, {
                params: {
                    start: startDate?.isValid() ? startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss') : null,
                    end: endDate?.isValid() ? endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss') : null,
                },
            })
            .then((data) => {
                setSummary(data);
            });
    }, [startDate, endDate]);

    const columns = useMemo(
        () => [
            {
                Header: 'GGR',
                accessor: 'ggr_manager',
                Cell: ({ value }) => formatCurrency(value, 2),
            },
            {
                Header: () => (
                    <div>
                        Commission{' '}
                        <QuestionMark definition="Formula: (GGR - (GGR * provider commission [15%])) * commission percentage" />
                    </div>
                ),
                accessor: 'daily_commission_manager',
                Cell: ({ value }) => formatCurrency(value, 2),
            },
            {
                Header: 'Used commission percentage',
                accessor: 'commission_percentage',
                Cell: ({ value }) => `${value}%`,
            },
            {
                Header: 'Date',
                accessor: 'calculated_date',
                Cell: ({ value }) => formatDate(value),
            },
        ],
        []
    );

    const getContent = () => {
        return (
            <Card title="Casino commissions">
                <TableLoader isLoading={isLoading}>
                    <DateSelectRange
                        containerClasses="d-flex"
                        range={range}
                        setRange={setRange}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                    />
                    <Table columns={columns} data={items} additionalClasses="model-list" />
                    <table className="table model-list">
                        <thead />
                        <tbody>
                            <tr className="table-summary-light">
                                <td className="w-25">{formatCurrency(summary.sum_ggr_manager ?? 0, 2)}</td>
                                <td className="w-25">{formatCurrency(summary.sum_daily_commission_manager ?? 0, 2)}</td>
                                <td />
                                <td />
                            </tr>
                        </tbody>
                    </table>
                </TableLoader>
                <Paginator {...auto.paginator} />
            </Card>
        );
    };
    return <Loader loading={isLoading} content={getContent} />;
}

export default CasinoAffiliateCommissions;
